import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Container,
  Grid,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatClear,
  TableChart,
  BorderVertical,
  BorderHorizontal,
  DeleteOutline,
  Save,
  Cancel,
  Info as InfoIcon,
  Description as DescriptionIcon,
  AccountTree as RelationsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { apiClient } from '../../api/apiClient';
import { useAuth } from '../../auth/AuthContext';

import Autocomplete from '@mui/material/Autocomplete';

// Bundesländer aus dem Backend-Schema
const BUNDESLAENDER = {
  BWB: "Baden-Württemberg",
  FSB: "Bayern",
  BLN: "Berlin",
  BRB: "Brandenburg",
  BRE: "Bremen",
  HAM: "Hamburg",
  HES: "Hessen",
  MVP: "Mecklenburg-Vorpommern",
  NSB: "Niedersachsen",
  NRW: "Nordrhein-Westfalen",
  RPF: "Rheinland-Pfalz",
  SAA: "Saarland",
  FSS: "Sachsen",
  SAN: "Sachsen-Anhalt",
  SHS: "Schleswig-Holstein",
  FST: "Thüringen",
  BRD: "Bundesrepublik Deutschland"
};

// Update editor styles
const editorStyles = {
  '.ProseMirror': {
    minHeight: '300px',
    padding: '1rem',
    backgroundColor: '#fff',
    overflowY: 'auto',
    '&:focus': {
      outline: 'none',
    },
    '& table': {
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      width: '100%',
      margin: '0',
      '& td, & th': {
        minWidth: '1em',
        border: '2px solid #ced4da',
        padding: '3px 5px',
        verticalAlign: 'top',
        boxSizing: 'border-box',
        position: 'relative',
        '& > *': {
          margin: 0,
        },
      },
    },
  },
};

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const handleButtonClick = (callback) => (e) => {
    e.preventDefault();
    callback();
  };

  return (
    <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBold().run())}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleItalic().run())}
        >
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive('underline')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleUnderline().run())}
        >
          <FormatUnderlined />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBulletList().run())}
        >
          <FormatListBulleted />
        </ToggleButton>
        <ToggleButton
          value="orderedList"
          selected={editor.isActive('orderedList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleOrderedList().run())}
        >
          <FormatListNumbered />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small" exclusive>
        <ToggleButton
          value="left"
          selected={editor.isActive({ textAlign: 'left' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('left').run())}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value="center"
          selected={editor.isActive({ textAlign: 'center' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('center').run())}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value="right"
          selected={editor.isActive({ textAlign: 'right' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('right').run())}
        >
          <FormatAlignRight />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="insertTable"
          onMouseDown={handleButtonClick(() => 
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          )}
        >
          <TableChart />
        </ToggleButton>
        <ToggleButton
          value="addColumnBefore"
          onMouseDown={handleButtonClick(() => editor.chain().focus().addColumnBefore().run())}
          disabled={!editor.can().addColumnBefore()}
        >
          <BorderVertical />
        </ToggleButton>
        <ToggleButton
          value="addRowBefore"
          onMouseDown={handleButtonClick(() => editor.chain().focus().addRowBefore().run())}
          disabled={!editor.can().addRowBefore()}
        >
          <BorderHorizontal />
        </ToggleButton>
        <ToggleButton
          value="deleteTable"
          onMouseDown={handleButtonClick(() => editor.chain().focus().deleteTable().run())}
          disabled={!editor.can().deleteTable()}
        >
          <DeleteOutline />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButton
        value="clear"
        onMouseDown={handleButtonClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}
      >
        <FormatClear />
      </ToggleButton>
    </Box>
  );
};

const SectionTitle = ({ icon: Icon, title }) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1, 
    mb: 2,
    mt: 3,
    color: 'primary.main',
    '&:first-of-type': {
      mt: 0
    }
  }}>
    <Icon />
    <Typography variant="h6">{title}</Typography>
  </Box>
);

const SimpleMenuBar = ({ editor }) => {
  if (!editor) return null;

  const handleButtonClick = (callback) => (e) => {
    e.preventDefault();
    callback();
  };

  return (
    <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBold().run())}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleItalic().run())}
        >
          <FormatItalic />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBulletList().run())}
        >
          <FormatListBulleted />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButton
        value="clear"
        onMouseDown={handleButtonClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}
      >
        <FormatClear />
      </ToggleButton>
    </Box>
  );
};

const SimpleEditor = ({ content, onChange, minRows = 4 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <Paper 
      sx={{ 
        border: '1px solid rgba(0, 0, 0, 0.23)',
        mb: 2,
      }}
    >
      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <SimpleMenuBar editor={editor} />
      </Box>
      <Box sx={{
        '.ProseMirror': {
          minHeight: `${minRows * 1.5}em`,
          padding: '1rem',
          backgroundColor: '#fff',
          overflowY: 'auto',
          '&:focus': {
            outline: 'none',
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

export default function EntscheidungEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData } = useAuth();
  const [gerichte, setGerichte] = useState([]);
  const [alleGerichte, setAlleGerichte] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    aktenzeichen: '',
    aktenzeichen_2_instanz: '',
    aktenzeichen_3_instanz: '',
    datum: '',
    datum_2_instanz: '',
    datum_3_instanz: '',
    sachgebiet: '',
    text: '',
    gericht_id: '',
    kategorie: '',
    leitsaetze: '',
    deskriptoren: '',
    normenkette: '',
    abteilung: '',
    land: '',
    bemerkung: '',
    active: true
  });
  const [sachgebiete, setSachgebiete] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'table'],
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'my-custom-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        text: editor.getHTML()
      }));
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gerichteResponse, alleGerichteResponse, rolesResponse, entscheidungResponse] = await Promise.all([
          apiClient.get('/gerichte/?assigned=true', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          apiClient.get('/gerichte/', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          apiClient.get('/roles/', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          apiClient.get(`/entscheidungen/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);

        setGerichte(gerichteResponse.data);
        setAlleGerichte(alleGerichteResponse.data);
        setRoles(rolesResponse.data);
        
        const entscheidung = entscheidungResponse.data;
        console.log('Geladene Entscheidung:', entscheidung);
        console.log('Verfügbare Gerichte:', gerichteResponse.data);
        console.log('Gerichts-IDs:', {
          gericht_id: entscheidung.gericht_id,
          gericht_1_instanz_id: entscheidung.gericht_1_instanz_id,
          gericht_2_instanz_id: entscheidung.gericht_2_instanz_id,
          gericht_3_instanz_id: entscheidung.gericht_3_instanz_id
        });
        
        // Zuerst die verfügbaren Optionen setzen
        if (entscheidungResponse.data.available_sachgebiete) {
          console.log('Verfügbare Sachgebiete:', entscheidungResponse.data.available_sachgebiete);
          setSachgebiete(entscheidungResponse.data.available_sachgebiete);
        }
        if (entscheidungResponse.data.available_kategorien) {
          console.log('Verfügbare Kategorien:', entscheidungResponse.data.available_kategorien);
          setKategorien(entscheidungResponse.data.available_kategorien);
        }
        
        // Dann erst die Formularwerte setzen
        const newFormData = {
          aktenzeichen: entscheidung.aktenzeichen || '',
          aktenzeichen_1_instanz: entscheidung.aktenzeichen_1_instanz || '',
          aktenzeichen_2_instanz: entscheidung.aktenzeichen_2_instanz || '',
          aktenzeichen_3_instanz: entscheidung.aktenzeichen_3_instanz || '',
          datum: entscheidung.datum_1_instanz?.split('T')[0] || '',
          datum_2_instanz: entscheidung.datum_2_instanz?.split('T')[0] || '',
          datum_3_instanz: entscheidung.datum_3_instanz?.split('T')[0] || '',
          sachgebiet: entscheidung.sachgebiet || '',
          text: entscheidung.text || '',
          gericht_id: entscheidung.gericht_id || '',
          gericht_1_instanz_id: entscheidung.gericht_1_instanz_id || '',
          gericht_2_instanz_id: entscheidung.gericht_2_instanz_id || '',
          gericht_3_instanz_id: entscheidung.gericht_3_instanz_id || '',
          kategorie: entscheidung.kategorie || '',
          leitsaetze: entscheidung.leitsaetze || '',
          deskriptoren: entscheidung.deskriptoren || '',
          normenkette: entscheidung.normenkette || '',
          abteilung: entscheidung.abteilung || '',
          land: entscheidung.land || '',
          bemerkung: entscheidung.bemerkung || '',
          active: entscheidung.active !== undefined ? entscheidung.active : true
        };
        
        // Alle Gerichte aus der Entscheidung zur Liste der verfügbaren Gerichte hinzufügen
        let alleGerichteList = [...alleGerichteResponse.data];
        if (entscheidung.gericht && !alleGerichteList.some(g => g.id === entscheidung.gericht.id)) {
          alleGerichteList.push(entscheidung.gericht);
        }
        if (entscheidung.gericht_1_instanz && !alleGerichteList.some(g => g.id === entscheidung.gericht_1_instanz.id)) {
          alleGerichteList.push(entscheidung.gericht_1_instanz);
        }
        if (entscheidung.gericht_2_instanz && !alleGerichteList.some(g => g.id === entscheidung.gericht_2_instanz.id)) {
          alleGerichteList.push(entscheidung.gericht_2_instanz);
        }
        if (entscheidung.gericht_3_instanz && !alleGerichteList.some(g => g.id === entscheidung.gericht_3_instanz.id)) {
          alleGerichteList.push(entscheidung.gericht_3_instanz);
        }
        setAlleGerichte(alleGerichteList);
        
        // Validiere, dass die Gerichts-IDs in der Liste der verfügbaren Gerichte enthalten sind
        const verfügbareGerichtsIds = alleGerichteList.map(gericht => gericht.id);
        console.log('Verfügbare Gerichts-IDs:', verfügbareGerichtsIds);
        
        if (newFormData.gericht_id && !verfügbareGerichtsIds.includes(parseInt(newFormData.gericht_id, 10))) {
          console.log(`Ungültige gericht_id: ${newFormData.gericht_id}`);
          newFormData.gericht_id = '';
        }
        
        if (newFormData.gericht_1_instanz_id && !verfügbareGerichtsIds.includes(parseInt(newFormData.gericht_1_instanz_id, 10))) {
          console.log(`Ungültige gericht_1_instanz_id: ${newFormData.gericht_1_instanz_id}`);
          newFormData.gericht_1_instanz_id = '';
        }
        
        if (newFormData.gericht_2_instanz_id && !verfügbareGerichtsIds.includes(parseInt(newFormData.gericht_2_instanz_id, 10))) {
          console.log(`Ungültige gericht_2_instanz_id: ${newFormData.gericht_2_instanz_id}`);
          newFormData.gericht_2_instanz_id = '';
        }
        
        if (newFormData.gericht_3_instanz_id && !verfügbareGerichtsIds.includes(parseInt(newFormData.gericht_3_instanz_id, 10))) {
          console.log(`Ungültige gericht_3_instanz_id: ${newFormData.gericht_3_instanz_id}`);
          newFormData.gericht_3_instanz_id = '';
        }
        
        setFormData(newFormData);
        
        // Editor Inhalt setzen
        if (editor && newFormData.text) {
          editor.commands.setContent(newFormData.text);
        }

        setLoading(false);
      } catch (error) {
        console.error('Fehler beim Laden der Daten:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, editor]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    
    try {
      const apiData = {
        aktenzeichen: formData.aktenzeichen,
        aktenzeichen_1_instanz: formData.aktenzeichen_1_instanz,
        aktenzeichen_2_instanz: formData.aktenzeichen_2_instanz,
        aktenzeichen_3_instanz: formData.aktenzeichen_3_instanz,
        datum_1_instanz: formData.datum,
        datum_2_instanz: formData.datum_2_instanz,
        datum_3_instanz: formData.datum_3_instanz,
        sachgebiet: formData.sachgebiet,
        text: formData.text,
        gericht_id: formData.gericht_id,
        gericht_1_instanz_id: formData.gericht_1_instanz_id || null,
        gericht_2_instanz_id: formData.gericht_2_instanz_id || null,
        gericht_3_instanz_id: formData.gericht_3_instanz_id || null,
        kategorie: formData.kategorie,
        leitsaetze: formData.leitsaetze,
        deskriptoren: formData.deskriptoren,
        normenkette: formData.normenkette,
        abteilung: formData.abteilung,
        land: formData.land,
        bemerkung: formData.bemerkung,
        active: formData.active
      };

      await apiClient.put(`/entscheidungen/${id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Wenn der Tab mit mittlerer Maustaste geöffnet wurde (opener existiert)
      if (window.opener) {
        // Schließe den aktuellen Tab
        window.close();
      } else {
        // Weiterleitung zur öffentlichen Entscheidungsseite
        navigate(`/entscheidungen/${id}`);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      
      // Detaillierte Fehlerbehandlung
      let errorMessage = "Ein unbekannter Fehler ist aufgetreten";
      let errorDetails = [];

      if (error.response) {
        // Server hat geantwortet, aber mit einem Fehlercode
        const { status, data } = error.response;
        
        if (data?.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            // ValidationError mit mehreren Fehlern
            errorMessage = "Validierungsfehler";
            errorDetails = data.detail.map(err => {
              if (typeof err === 'object' && err.msg && err.loc) {
                return `${err.loc.join('.')}: ${err.msg}`;
              }
              return typeof err === 'string' ? err : JSON.stringify(err);
            });
          } else if (typeof data.detail === 'object') {
            errorMessage = data.detail.message || "Fehler beim Speichern";
            if (data.detail.errors) {
              errorDetails = Array.isArray(data.detail.errors) 
                ? data.detail.errors 
                : [data.detail.errors];
            }
          }
        } else {
          // Fallback für verschiedene HTTP-Status-Codes
          switch (status) {
            case 400:
              errorMessage = "Ungültige Daten übermittelt";
              break;
            case 401:
              errorMessage = "Nicht autorisiert - bitte melden Sie sich erneut an";
              break;
            case 403:
              errorMessage = "Keine Berechtigung für diese Aktion";
              break;
            case 404:
              errorMessage = "Entscheidung nicht gefunden";
              break;
            case 422:
              errorMessage = "Validierungsfehler bei den übermittelten Daten";
              break;
            case 500:
              errorMessage = "Interner Serverfehler - bitte versuchen Sie es später erneut";
              break;
            default:
              errorMessage = `Fehler ${status}: ${data?.message || 'Unbekannter Serverfehler'}`;
          }
        }
      } else if (error.request) {
        // Request wurde gesendet, aber keine Antwort erhalten
        errorMessage = "Keine Verbindung zum Server - bitte prüfen Sie Ihre Internetverbindung";
      } else {
        // Fehler beim Erstellen des Requests
        errorMessage = error.message || "Fehler beim Senden der Anfrage";
      }

      setSaveError({
        message: errorMessage,
        details: errorDetails
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const isAdmin = roles.find(r => r.id === userData?.role_id)?.permissions?.some(p => p.name === 'admin');

  const handleEditorChange = (field) => (content) => {
    setFormData(prev => ({
      ...prev,
      [field]: content
    }));
  };

  // Alphabetisch sortierte Gerichte
  const sortedGerichte = [...gerichte].sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));
  const sortedAlleGerichte = [...alleGerichte].sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Entscheidung bearbeiten</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={saving}
              >
                Abbrechen
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </Box>
          </Box>

          {/* Fehleranzeige */}
          {saveError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {saveError.message}
              </Typography>
              {saveError.details && saveError.details.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">Details:</Typography>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    {saveError.details.map((detail, index) => (
                      <li key={index}>
                        <Typography variant="body2">{detail}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(event) => {
                    setFormData(prev => ({
                      ...prev,
                      active: event.target.checked
                    }));
                  }}
                  color="primary"
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    {formData.active ? "Veröffentlicht" : "Nicht veröffentlicht"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formData.active 
                      ? "Die Entscheidung ist öffentlich sichtbar" 
                      : "Die Entscheidung ist nur intern sichtbar"}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <form>
            <Grid container spacing={3}>
              {/* Basisinfos */}
              <Grid item xs={12}>
                <SectionTitle icon={InfoIcon} title="Basisinfos" />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen"
                      label="Aktenzeichen"
                      value={formData.aktenzeichen}
                      onChange={handleInputChange}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedGerichte.find(g => g.id === formData.gericht_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht" required fullWidth />
                      )}
                      disabled={!isAdmin && userData?.gericht_id}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Kategorie</InputLabel>
                      <Select
                        value={formData.kategorie || ''}
                        onChange={(e) => handleInputChange({ target: { name: 'kategorie', value: e.target.value }})}
                        label="Kategorie"
                      >
                        <MenuItem value="">
                          <em>Keine Kategorie</em>
                        </MenuItem>
                        {kategorien.map((kat) => (
                          <MenuItem key={kat.code} value={kat.code}>
                            {kat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Sachgebiet</InputLabel>
                      <Select
                        value={formData.sachgebiet || ''}
                        onChange={(e) => handleInputChange({ target: { name: 'sachgebiet', value: e.target.value }})}
                        label="Sachgebiet"
                      >
                        <MenuItem value="">
                          <em>Kein Sachgebiet</em>
                        </MenuItem>
                        {sachgebiete.map((sg) => (
                          <MenuItem key={sg.code} value={sg.code}>
                            {sg.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <TextField
                        name="abteilung"
                        label="Abteilung"
                        value={formData.abteilung}
                        onChange={handleInputChange}
                        fullWidth
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Land</InputLabel>
                      <Select
                        name="land"
                        value={formData.land}
                        onChange={handleInputChange}
                        label="Land"
                      >
                        <MenuItem value="">
                          <em>Bitte wählen</em>
                        </MenuItem>
                        {BUNDESLAENDER && Object.entries(BUNDESLAENDER).map(([code, name]) => (
                          <MenuItem key={code} value={code}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Relationen */}
              <Grid item xs={12}>
                <SectionTitle icon={RelationsIcon} title="Relationen" />
                <Grid container spacing={3}>
                  {/* Erste Instanz */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen_1_instanz"
                      label="Aktenzeichen (1. Instanz)"
                      value={formData.aktenzeichen_1_instanz}
                      onChange={handleInputChange}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="datum"
                      label="Datum (1. Instanz)"
                      type="date"
                      value={formData.datum}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht 1. Instanz Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedAlleGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedAlleGerichte.find(g => g.id === formData.gericht_1_instanz_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_1_instanz_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht (1. Instanz)" required fullWidth />
                      )}
                      disabled={!isAdmin && userData?.gericht_id}
                    />
                  </Grid>

                  {/* Zweite Instanz */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen_2_instanz"
                      label="Aktenzeichen (2. Instanz)"
                      value={formData.aktenzeichen_2_instanz}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="datum_2_instanz"
                      label="Datum (2. Instanz)"
                      type="date"
                      value={formData.datum_2_instanz}
                      onChange={handleInputChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht 2. Instanz Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedAlleGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedAlleGerichte.find(g => g.id === formData.gericht_2_instanz_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_2_instanz_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht (2. Instanz)" fullWidth />
                      )}
                    />
                  </Grid>

                  {/* Dritte Instanz */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen_3_instanz"
                      label="Aktenzeichen (3. Instanz)"
                      value={formData.aktenzeichen_3_instanz}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="datum_3_instanz"
                      label="Datum (3. Instanz)"
                      type="date"
                      value={formData.datum_3_instanz}
                      onChange={handleInputChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht 3. Instanz Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedAlleGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedAlleGerichte.find(g => g.id === formData.gericht_3_instanz_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_3_instanz_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht (3. Instanz)" fullWidth />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Entscheidungstext */}
              <Grid item xs={12}>
                <SectionTitle icon={DescriptionIcon} title="Entscheidungstext" />
                <Paper 
                  sx={{ 
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    mb: 2,
                  }}
                >
                  <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                    <MenuBar editor={editor} />
                  </Box>
                  <Box sx={editorStyles}>
                    <EditorContent editor={editor} />
                  </Box>
                </Paper>
              </Grid>

              {/* Zusätzliche Informationen */}
              <Grid item xs={12}>
                <SectionTitle icon={SettingsIcon} title="Zusätzliche Informationen" />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Leitsätze</Typography>
                    <SimpleEditor
                      content={formData.leitsaetze}
                      onChange={handleEditorChange('leitsaetze')}
                      minRows={4}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Deskriptoren</Typography>
                    <SimpleEditor
                      content={formData.deskriptoren}
                      onChange={handleEditorChange('deskriptoren')}
                      minRows={4}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Normenkette</Typography>
                    <SimpleEditor
                      content={formData.normenkette}
                      onChange={handleEditorChange('normenkette')}
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Bemerkung</Typography>
                    <SimpleEditor
                      content={formData.bemerkung}
                      onChange={handleEditorChange('bemerkung')}
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>

          {/* Buttons am unteren Rand */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2,
            mt: 4,
            pt: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.12)'
          }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
} 